/**
 * 文壇サロン（/salon）— SALON_SPEC §5 画面仕様
 *
 * β=3機能: SLN-01 制作ノート / SLN-02 私の書架 / SLN-03 合評ルーム（署名制インライン注釈・決定7）。
 * anokoe-design: 事実性①>a11y②>タスク③>演出④>美観⑤。主CTA1つ・アクション4つ以内・4状態・44px・
 * reduced-motion代替・YES/NO禁止（文言変換表）。書き手街区だが台本色を過度に主役化しない。
 */
import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { api, SalonNote, SalonShelfItem, SalonAnnotation, TownPrompt } from '../services/api';
import { SubmissionModal } from '../components/SubmissionModal';
import { PulseButton } from '../components/ui/PulseButton';
import { useGamenics, GamenicsEvent } from '../hooks/useGamenics';
import { TownShell, TownLoading, TownError, TownEmpty, TownGoals, useTownUser, timeAgo } from '../components/town/townShared';

type Load = 'loading' | 'ready' | 'error';

// ══════════════════════════════════════════════════════════
// ラウンジ /salon
// ══════════════════════════════════════════════════════════
export const Salon: FC = () => {
  const [, setLocation] = useLocation();
  const { user, requireLogin, loginGate } = useTownUser();
  const { playSound } = useGamenics();
  const [notes, setNotes] = useState<SalonNote[]>([]);
  const [state, setState] = useState<Load>('loading');
  const [composing, setComposing] = useState(false);
  // PIPE-03 逆導線: 作品詳細から ?from_post=<id> で来たら、その作品に紐づけてノートを綴り始める
  const [linkedPostId, setLinkedPostId] = useState<string | null>(null);
  // SLN-05「今週の一筆」（Admin手動運用・決定19）。null = 掲出中のお題なし＝「準備中」表示（決定3）
  const [prompt, setPrompt] = useState<TownPrompt | null>(null);
  const [promptLoading, setPromptLoading] = useState(true);
  const [promptSeed, setPromptSeed] = useState<string | null>(null);

  const load = () => {
    setState('loading');
    api.salonListNotes()
      .then(rows => { setNotes(rows); setState('ready'); })
      .catch(() => setState('error'));
  };
  useEffect(load, []);

  useEffect(() => {
    api.salonCurrentPrompt()
      .then(p => setPrompt(p))
      .finally(() => setPromptLoading(false));
  }, []);

  useEffect(() => {
    const fromPost = new URLSearchParams(window.location.search).get('from_post');
    // ログイン済みのときだけ自動で開く（未ログインは通常の「ノートを綴る」からログイン導線へ）
    if (fromPost && user) { setLinkedPostId(fromPost); setComposing(true); }
  }, [user]);

  return (
    <TownShell
      role="script"
      kicker="WRITERS' LOUNGE"
      title="文壇サロン"
      actions={
        <div className="flex gap-2">
          <button className="btn btn-ghost btn-sm min-h-[44px]" onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setLocation('/salon/board'); }}>
            掲示板
          </button>
          {user && (
            <button className="btn btn-ghost btn-sm min-h-[44px]" onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setLocation(`/salon/shelves/${user.id}`); }}>
              私の書架へ
            </button>
          )}
        </div>
      }
    >
      <p className="text-ink-2 text-sm mb-6 max-w-2xl">
        語り合いが、次の一行を生む。「書いた後」ではなく「書く手前と最中」を支える円卓ラウンジ。
      </p>

      {/* SLN-05 今週の一筆（Admin手動運用・決定19）。未設定・切替漏れは正直に「準備中」＝偽のお題を出さない（決定3） */}
      <div className="pop-card p-5 mb-6">
        <span className="text-[11px] font-bold text-role-script-text uppercase tracking-wide">今週の一筆</span>
        {promptLoading ? (
          <div className="mt-2 h-6 w-56 bg-cream-2 rounded motion-safe:animate-pulse" aria-label="読み込み中" />
        ) : prompt ? (
          <>
            <h2 className="font-display font-bold text-ink text-lg mt-1">{prompt.title}</h2>
            {prompt.description && <p className="text-sm text-ink-2 mt-1">{prompt.description}</p>}
            <div className="mt-3">
              <button
                className="btn btn-ghost min-h-[44px]"
                onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); requireLogin(() => { setPromptSeed(prompt.title); setComposing(true); }); }}
              >
                この題で綴る
              </button>
            </div>
          </>
        ) : (
          <p className="text-ink-2 mt-2 text-sm">次のお題を準備中です。いまは自由に一篇どうぞ。</p>
        )}
      </div>

      <div className="mb-6">
        <PulseButton isPrimary onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); requireLogin(() => { setPromptSeed(null); setComposing(true); }); }}>
          ノートを綴る
        </PulseButton>
      </div>

      {user && <TownGoals role="script" />}

      {state === 'loading' && <TownLoading />}
      {state === 'error' && <TownError onRetry={load} />}
      {state === 'ready' && notes.length === 0 && (
        <TownEmpty title="まだ、綴られたノートはありません。最初の一篇をあなたから。">
          <PulseButton isPrimary onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); requireLogin(() => setComposing(true)); }}>
            ノートを綴る
          </PulseButton>
        </TownEmpty>
      )}
      {state === 'ready' && notes.length > 0 && (
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4 list-none p-0 m-0">
          {notes.map(n => (
            <li key={n.id}>
              <button
                onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setLocation(`/salon/notes/${n.id}`); }}
                className="pop-card w-full text-left p-5 hover:-translate-y-0.5 transition-transform min-h-[44px]"
              >
                <div className="flex items-center gap-2 mb-2">
                  <h2 className="font-display font-bold text-ink text-base flex-1">{n.title || '無題のノート'}</h2>
                  {n.published_post_id && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-role-script-soft text-role-script-text border border-role-script">作品化済み</span>
                  )}
                </div>
                <p className="text-sm text-ink-2 line-clamp-3 whitespace-pre-wrap">{n.body}</p>
                <span className="block mt-3 text-[11px] text-ink-3 tabular-nums">{timeAgo(n.created_at)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {composing && (
        <NoteComposer
          linkedPostId={linkedPostId}
          promptTitle={promptSeed}
          onClose={() => setComposing(false)}
          onSaved={(id) => { setComposing(false); if (id) setLocation(`/salon/notes/${id}`); else load(); }}
        />
      )}
      {loginGate}
    </TownShell>
  );
};

// ── ノート作成（SLN-01） ──
const NoteComposer: FC<{ linkedPostId?: string | null; promptTitle?: string | null; onClose: () => void; onSaved: (id?: string) => void }> = ({ linkedPostId, promptTitle, onClose, onSaved }) => {
  const { playSound } = useGamenics();
  // 「この題で綴る」から来たときはタイトルにお題を初期投入（本文は本人が書く）
  const [title, setTitle] = useState(promptTitle ?? '');
  const [body, setBody] = useState('');
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (status: 'draft' | 'public') => {
    if (!body.trim()) { setError('想いを一言だけでも綴ってください'); return; }
    setSaving(true); setError(null);
    const r = await api.salonCreateNote({ title: title || undefined, body, draft_text: draft || undefined, linked_post_id: linkedPostId ?? undefined, status });
    setSaving(false);
    if (r.success) { playSound(GamenicsEvent.SUBMISSION_COMPLETE); onSaved(r.id); }
    else setError(r.error || 'うまく保存できませんでした');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface border-2 border-ink rounded-pop p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-display font-bold mb-4">制作ノートを綴る</h2>
        {/* PIPE-03: 作品詳細から持ち込まれた文脈（この作品に紐づけて保存される） */}
        {linkedPostId && (
          <p className="text-[11px] text-role-script-text bg-role-script-soft border border-role-script rounded px-2 py-1 mb-3">
            あの作品に続けて綴ります（この作品に紐づきます）
          </p>
        )}
        {/* SLN-05: 今週の一筆から始めた場合の文脈表示 */}
        {promptTitle && (
          <p className="text-[11px] text-role-script-text bg-role-script-soft border border-role-script rounded px-2 py-1 mb-3">
            今週の一筆「{promptTitle}」で綴ります
          </p>
        )}
        <label className="block text-sm text-ink-2 mb-1">タイトル（任意）</label>
        <input value={title} onChange={e => setTitle(e.target.value)} maxLength={100}
          className="pop-field w-full mb-4" placeholder="このノートの名前" />
        <label className="block text-sm text-ink-2 mb-1">想い・こだわり・裏話</label>
        <textarea value={body} onChange={e => setBody(e.target.value)} rows={4}
          className="pop-field w-full mb-4 resize-none" placeholder="作品の裏側を、思うままに。" />
        <label className="block text-sm text-ink-2 mb-1">執筆テキスト（任意・台本の下書き）</label>
        <textarea value={draft} onChange={e => setDraft(e.target.value)} rows={5}
          className="pop-field w-full mb-2 resize-none" placeholder="ここに書いた文章は、あとで「作品として届ける」ことができます。" />
        {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
        <div className="flex gap-3 mt-3">
          <button className="btn btn-ghost flex-1 min-h-[44px]" disabled={saving} onClick={() => { playSound(GamenicsEvent.TAP_CANCEL); save('draft'); }}>
            大切にしまう
          </button>
          <PulseButton isPrimary className="flex-1" onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); save('public'); }}>
            みんなに公開する
          </PulseButton>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════
// ノート詳細 /salon/notes/:id （SLN-01 + SLN-03 署名制インライン注釈）
// ══════════════════════════════════════════════════════════
export const SalonNoteDetail = ({ params }: any) => {
  const id: string = params?.id;
  const [, setLocation] = useLocation();
  const { user, requireLogin, loginGate } = useTownUser();
  const { playSound } = useGamenics();
  // PIPE-02: ステージへ持ち込む（参照インポート）
  const [importing, setImporting] = useState(false);
  const [importCredit, setImportCredit] = useState<string | null>(null);
  const [note, setNote] = useState<SalonNote | null>(null);
  const [state, setState] = useState<Load>('loading');
  const [annotations, setAnnotations] = useState<SalonAnnotation[]>([]);
  const [pipeOpen, setPipeOpen] = useState(false);
  const [pendingRange, setPendingRange] = useState<{ start: number; end: number } | null>(null);
  const [annoText, setAnnoText] = useState('');
  const bodyRef = useRef<HTMLDivElement>(null);

  const load = () => {
    setState('loading');
    api.salonGetNote(id)
      .then(n => {
        if (!n) { setState('error'); return; }
        setNote(n); setState('ready');
      })
      .catch(() => setState('error'));
    api.salonListAnnotations(id).then(setAnnotations);
  };
  useEffect(load, [id]);

  const isOwner = !!user && !!note && user.id === note.author_id;
  // 公開＋執筆テキストありなら誰でもステージへ参照インポートできる（STAGE_SPEC §8 PIPE-02・原作者は自動クレジット）
  const canBringToStage = !!note && note.status === 'public' && !!note.draft_text;

  const bringToStage = () => requireLogin(async () => {
    if (!note) return;
    setImporting(true);
    const r = await api.importScriptFromSalon(note.id);
    setImporting(false);
    if (!r.success || !r.id) { setImportCredit(null); alert(r.error || 'ステージへ持ち込めませんでした'); return; }
    playSound(GamenicsEvent.TAP_CONFIRM);
    // 「一瞬の間」を置いてクレジット確定を静かに提示してから遷移（reduced-motion時はアニメ無しの静的表示）
    const credited = await api.getPublicUser(note.author_id);
    setImportCredit(credited?.display_name || '原作者'); // 解決できなければ名前を捏造せず既定表示
    setTimeout(() => setLocation(`/stage/scripts/${r.id}`), 900);
  });

  // 本文中の一節を選択 → 注釈対象レンジを取得（署名制注釈の対象／SLN-03）
  const onSelectText = () => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || !bodyRef.current) { setPendingRange(null); return; }
    const range = sel.getRangeAt(0);
    if (!bodyRef.current.contains(range.commonAncestorContainer)) return;
    const start = Math.min(range.startOffset, range.endOffset);
    const end = Math.max(range.startOffset, range.endOffset);
    if (end > start) setPendingRange({ start, end });
  };

  const submitAnnotation = async () => {
    if (!annoText.trim()) return;
    const r = await api.salonAddAnnotation(id, { body: annoText, range_start: pendingRange?.start, range_end: pendingRange?.end });
    if (r.success) {
      playSound(GamenicsEvent.TAP_CONFIRM);
      setAnnoText(''); setPendingRange(null);
      api.salonListAnnotations(id).then(setAnnotations);
    }
  };

  // 注釈レンジで本文をハイライト分割（再表示時のハイライト・SLN-03受入基準）
  const bodyText = note?.body ?? '';
  const highlighted = useMemo(() => renderWithHighlights(bodyText, annotations), [bodyText, annotations]);

  return (
    <TownShell role="script" kicker="WRITERS' LOUNGE" title="制作ノート">
      {state === 'loading' && <TownLoading />}
      {state === 'error' && <TownError message="このノートを開けませんでした" onRetry={load} />}
      {state === 'ready' && note && (
        <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-6">
          {/* 本文 */}
          <div>
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <h2 className="font-display font-bold text-lg text-ink flex-1">{note.title || '無題のノート'}</h2>
              {note.published_post_id && (
                <button
                  onClick={() => setLocationSafe(`/post/${note.published_post_id}`)}
                  className="text-[11px] font-bold px-2 py-1 rounded-full bg-role-script-soft text-role-script-text border border-role-script min-h-[44px]"
                >
                  作品化済み — 作品を見る
                </button>
              )}
            </div>

            <p className="text-[11px] text-ink-3 mb-2">本文の一節を選ぶと、その箇所に感想（署名付き注釈）を添えられます。</p>
            <div
              ref={bodyRef}
              onMouseUp={onSelectText}
              className="pop-card p-5 text-ink whitespace-pre-wrap leading-relaxed text-[15px] select-text"
            >
              {highlighted}
            </div>

            {note.draft_text && (
              <div className="mt-5">
                <h3 className="text-xs font-bold text-ink-2 mb-2 tracking-wide">執筆テキスト（台本の下書き）</h3>
                <div className="pop-card p-5 text-ink-2 whitespace-pre-wrap text-sm bg-cream">{note.draft_text}</div>
              </div>
            )}

            {/* 意思決定アクション（4つ以内・主CTA1つ） */}
            <div className="flex flex-wrap items-center gap-3 mt-6">
              <PulseButton
                isPrimary
                disabled={!note.draft_text}
                onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); requireLogin(() => setPipeOpen(true)); }}
              >
                {note.published_post_id ? '編集して再届け' : '作品として届ける'}
              </PulseButton>
              {/* PIPE-02: サロン台本→ステージ参照インポート（本文はステージ側で改稿されない＝決定11） */}
              {canBringToStage && (
                <button className="btn btn-ghost min-h-[44px]" disabled={importing} onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); bringToStage(); }}>
                  {importing ? '持ち込んでいます…' : 'ステージへ持ち込む'}
                </button>
              )}
              {!note.draft_text && <span className="text-[11px] text-ink-3">※ 執筆テキストを書くと「作品として届ける」が使えます</span>}
              {isOwner && (
                <button className="btn btn-ghost min-h-[44px]" onClick={() => { playSound(GamenicsEvent.TAP_CANCEL); if (confirm('このノートを手放しますか？（作品化済みの作品は残ります）')) { api.salonDeleteNote(id).then(() => setLocationSafe('/salon')); } }}>
                  手放す
                </button>
              )}
            </div>
          </div>

          {/* 合評（署名制注釈）SLN-03 */}
          <aside>
            <h3 className="text-xs font-bold text-ink-2 mb-3 tracking-wide">合評ルーム — そっと感想を送る</h3>
            {user ? (
              <div className="pop-card p-4 mb-4">
                {pendingRange && (
                  <p className="text-[11px] text-role-script-text bg-role-script-soft rounded px-2 py-1 mb-2">
                    「{bodyText.slice(pendingRange.start, pendingRange.end)}」に添えます
                  </p>
                )}
                <textarea value={annoText} onChange={e => setAnnoText(e.target.value)} rows={3}
                  className="pop-field w-full mb-2 resize-none text-sm" placeholder="優しい一言から。「この一行がいい」" />
                <div className="flex justify-end">
                  <PulseButton isPrimary onClick={submitAnnotation} disabled={!annoText.trim()}>注釈を添える</PulseButton>
                </div>
              </div>
            ) : (
              <button className="btn btn-ghost btn-sm mb-4 min-h-[44px]" onClick={() => requireLogin(() => {})}>ログインして感想を送る</button>
            )}

            {annotations.length === 0 ? (
              <p className="text-sm text-ink-3">まだ注釈はありません。最初の一言をあなたから。</p>
            ) : (
              <ul className="flex flex-col gap-3 list-none p-0 m-0">
                {annotations.map(a => (
                  <li key={a.id} className="pop-card p-3">
                    {a.range_start != null && a.range_end != null && (
                      <p className="text-[11px] text-role-script-text mb-1">「{bodyText.slice(a.range_start, a.range_end)}」</p>
                    )}
                    <p className="text-sm text-ink whitespace-pre-wrap">{a.body}</p>
                    <div className="flex items-center justify-between mt-2">
                      {/* 署名制（決定2）: 投稿者を必ず表示。匿名UIは無い */}
                      <span className="text-[11px] text-ink-3">署名: {a.author_id.slice(0, 8)}…</span>
                      {user && user.id === a.author_id && (
                        <button className="text-[11px] text-ink-3 underline min-h-[44px]" onClick={() => api.salonDeleteAnnotation(a.id).then(() => api.salonListAnnotations(id).then(setAnnotations))}>手放す</button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </aside>
        </div>
      )}

      {/* クレジット確定演出（PIPE-02）: 静かな確定表示。reduced-motion時はフェード無しの静的表示 */}
      {importCredit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="status" aria-live="polite">
          <div className="absolute inset-0 bg-ink/60" />
          <div className="relative bg-surface border-2 border-ink rounded-pop p-6 text-center motion-safe:animate-in motion-safe:fade-in">
            <p className="font-display font-bold text-ink mb-1">ステージへ持ち込みました</p>
            <p className="text-sm text-role-script-text bg-role-script-soft border border-role-script rounded px-3 py-1 inline-block">
              サロン発・原作: {importCredit}
            </p>
            <p className="text-[11px] text-ink-3 mt-3">本文はサロンの原文のまま参照されます（ステージ側で改稿されません）</p>
          </div>
        </div>
      )}

      {pipeOpen && note && (
        <SubmissionModal
          postId="genesis"
          role="script"
          initialText={note.draft_text || ''}
          initialTitle={note.title || ''}
          initialDescription={note.body}
          originRef={{ space: 'salon', item_id: note.id, version: note.version }}
          onClose={() => setPipeOpen(false)}
          onSuccess={() => { setPipeOpen(false); load(); }}
        />
      )}
      {loginGate}
    </TownShell>
  );
};

// wouter setLocation をコンポーネント外ヘルパから呼ぶための簡易ラッパ（history 経由）
const setLocationSafe = (path: string) => { window.history.pushState(null, '', path); window.dispatchEvent(new PopStateEvent('popstate')); };

/** 本文文字列を注釈レンジで <mark> 分割してハイライト（再表示時のハイライト・SLN-03） */
function renderWithHighlights(text: string, annotations: SalonAnnotation[]) {
  const ranges = annotations
    .filter(a => a.range_start != null && a.range_end != null && a.range_end! > a.range_start!)
    .map(a => ({ start: a.range_start!, end: a.range_end! }))
    .sort((x, y) => x.start - y.start);
  if (ranges.length === 0) return text;
  const out: ReactNode[] = [];
  let cursor = 0;
  ranges.forEach((r, i) => {
    if (r.start > cursor) out.push(text.slice(cursor, r.start));
    if (r.start >= cursor) {
      out.push(
        <mark key={i} className="bg-role-script-soft text-role-script-text rounded px-0.5">
          {text.slice(r.start, r.end)}
        </mark>
      );
      cursor = Math.max(cursor, r.end);
    }
  });
  if (cursor < text.length) out.push(text.slice(cursor));
  return out;
}

// ══════════════════════════════════════════════════════════
// 私の書架 /salon/shelves/:userId （SLN-02）
// ══════════════════════════════════════════════════════════
export const SalonShelf = ({ params }: any) => {
  const userId: string = params?.userId;
  const { user, requireLogin, loginGate } = useTownUser();
  const { playSound } = useGamenics();
  const [items, setItems] = useState<SalonShelfItem[]>([]);
  const [state, setState] = useState<Load>('loading');
  const [adding, setAdding] = useState(false);
  const isOwner = !!user && user.id === userId;

  const load = () => {
    setState('loading');
    api.salonListShelves(userId)
      .then(rows => { setItems(rows); setState('ready'); })
      .catch(() => setState('error'));
  };
  useEffect(load, [userId]);

  return (
    <TownShell role="script" kicker="MY BOOKSHELF" title="私の書架">
      {isOwner && (
        <div className="mb-6">
          <PulseButton isPrimary onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); requireLogin(() => setAdding(true)); }}>
            棚に一冊を加える
          </PulseButton>
        </div>
      )}

      {state === 'loading' && <TownLoading />}
      {state === 'error' && <TownError message="棚を開けませんでした" onRetry={load} />}
      {state === 'ready' && items.length === 0 && (
        <TownEmpty title={isOwner ? 'あなたの一冊を、最初の背表紙に。' : 'まだ棚は空のようです。'} />
      )}
      {state === 'ready' && items.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {items.map(it => (
            <div key={it.id} className="rounded-pop-sm border-2 border-ink bg-role-script-soft p-4 min-h-[120px] flex flex-col relative"
              style={{ boxShadow: 'var(--shadow-hard-sm, 3px 3px 0 var(--ink))' }}>
              {/* カバー画像は持たない（決定6）— 文字＋背表紙風プリセット図案のみ */}
              <span className="text-[10px] font-bold text-role-script-text uppercase tracking-wide">{it.category || '—'}</span>
              <span className="font-display font-bold text-ink text-sm mt-1 flex-1">{it.title}</span>
              {it.author_name && <span className="text-[11px] text-ink-2">{it.author_name}</span>}
              {it.note && <span className="text-[11px] text-ink-3 mt-1 line-clamp-2">{it.note}</span>}
              {isOwner && (
                <button className="absolute top-1.5 right-1.5 text-[11px] text-ink-3 underline min-h-[28px]" onClick={() => api.salonDeleteShelf(it.id).then(load)}>手放す</button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* 免責注記（決定18）— 常設表示 */}
      <p className="mt-8 text-[11px] text-ink-3">※各作品名・著者名は各権利者に帰属します。</p>

      {adding && (
        <ShelfComposer onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />
      )}
      {loginGate}
    </TownShell>
  );
};

const ShelfComposer: FC<{ onClose: () => void; onSaved: () => void }> = ({ onClose, onSaved }) => {
  const { playSound } = useGamenics();
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [category, setCategory] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!title.trim()) { setError('作品名を入れてください'); return; }
    const r = await api.salonAddShelf({ title, author_name: author || undefined, category: category || undefined, note: note || undefined });
    if (r.success) { playSound(GamenicsEvent.SUBMISSION_COMPLETE); onSaved(); }
    else setError(r.error || 'うまく加えられませんでした');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface border-2 border-ink rounded-pop p-6 w-full max-w-md">
        <h2 className="text-xl font-display font-bold mb-4">棚に一冊を加える</h2>
        <label className="block text-sm text-ink-2 mb-1">作品名</label>
        <input value={title} onChange={e => setTitle(e.target.value)} className="pop-field w-full mb-3" placeholder="影響を受けた作品名（外部作品もOK）" />
        <label className="block text-sm text-ink-2 mb-1">著者名（任意）</label>
        <input value={author} onChange={e => setAuthor(e.target.value)} className="pop-field w-full mb-3" placeholder="著者・作者名" />
        <label className="block text-sm text-ink-2 mb-1">カテゴリ（任意）</label>
        <input value={category} onChange={e => setCategory(e.target.value)} className="pop-field w-full mb-3" placeholder="純文学 / SF / 脚本 …" />
        <label className="block text-sm text-ink-2 mb-1">一言（任意）</label>
        <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} className="pop-field w-full mb-2 resize-none" placeholder="この一冊への想い" />
        <p className="text-[11px] text-ink-3 mb-3">※カバー画像は登録できません（文字＋背表紙デザインのみ）。※各作品名・著者名は各権利者に帰属します。</p>
        {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
        <div className="flex gap-3">
          <button className="btn btn-ghost flex-1 min-h-[44px]" onClick={() => { playSound(GamenicsEvent.TAP_CANCEL); onClose(); }}>やっぱりやめる</button>
          <PulseButton isPrimary className="flex-1" onClick={save}>棚に加える</PulseButton>
        </div>
      </div>
    </div>
  );
};
